import React, { useState, useEffect } from 'react';
import { Award, ShieldCheck, CheckCircle2, UserCheck, Briefcase, BookmarkCheck, Globe2, Sparkles } from 'lucide-react';
import { Language, SiteBranding } from '../types';
import { translations } from '../i18n/translations';
import { BrandLogo } from './BrandLogo';
import { getSiteBranding } from '../services/siteSettingsStorage';

interface AboutSectionProps {
  currentLang: Language;
}

export const AboutSection: React.FC<AboutSectionProps> = ({ currentLang }) => {
  const [branding, setBranding] = useState<SiteBranding>(getSiteBranding());
  const t = translations[currentLang];

  useEffect(() => {
    const handleBrandingChange = (e: Event) => {
      const customEvent = e as CustomEvent<SiteBranding>;
      if (customEvent.detail) {
        setBranding(customEvent.detail);
      } else {
        setBranding(getSiteBranding());
      }
    };

    window.addEventListener('chc_site_branding_updated', handleBrandingChange);
    return () => window.removeEventListener('chc_site_branding_updated', handleBrandingChange);
  }, []);

  return (
    <section id="apropos" className="py-20 bg-white dark:bg-[#061822] text-slate-900 dark:text-white relative overflow-hidden border-b border-slate-200 dark:border-[#0E3A4B] transition-colors duration-200">
      {/* Background Accent */}
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="w-[90%] max-w-none mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center w-[90%] lg:w-[70%] max-w-none mx-auto mb-16 space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 dark:bg-[#09222E] border border-cyan-300 dark:border-cyan-500/40 text-cyan-800 dark:text-cyan-300 text-xs font-bold uppercase tracking-wider shadow-sm">
            <Award className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
            <span>{t.about.badge}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold font-montserrat text-slate-900 dark:text-white">
            {branding.aboutTitle || t.about.title}
          </h2>
          <p className="text-sm sm:text-base text-cyan-700 dark:text-cyan-300 font-semibold tracking-wide">
            {t.about.founderTitle}
          </p>
        </div>

        {/* Grid: Founder Profile & Cabinet Details */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          
          {/* Founder Executive Card (Left 5 Cols) */}
          <div className="lg:col-span-5 space-y-6">
            <div className="relative rounded-3xl bg-slate-50 dark:bg-gradient-to-b dark:from-[#09222E] dark:to-[#040E14] p-6 sm:p-8 border border-slate-200 dark:border-cyan-500/40 shadow-xl dark:shadow-2xl overflow-hidden group">
              
              {/* Cyan decorative corner */}
              <div className="absolute top-0 right-0 rtl:right-auto rtl:left-0 w-28 h-28 bg-gradient-to-bl rtl:bg-gradient-to-br from-cyan-500/20 via-teal-500/10 to-transparent rounded-bl-full rtl:rounded-bl-none rtl:rounded-br-full pointer-events-none" />
              
              <div className="flex items-center gap-4 mb-6">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-400 via-sky-500 to-teal-500 p-0.5 shadow-lg shadow-cyan-500/30">
                  <div className="w-full h-full bg-white dark:bg-[#061822] rounded-[14px] flex items-center justify-center p-1">
                    <BrandLogo variant="icon-only" size="md" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold font-montserrat text-slate-900 dark:text-white tracking-tight">
                    Chérif Haddad
                  </h3>
                  <p className="text-xs text-cyan-700 dark:text-cyan-300 font-bold tracking-wider uppercase">
                    Lead Auditor IRCA & Senior Expert
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {currentLang === 'ar' ? 'استشارات • تدقيق • جودة وبيئة وسلامة • أمن موانئ' : currentLang === 'en' ? 'Consulting • Audit • QHSE • Maritime Security' : 'Conseil • Audit • QHSE • Sûreté Maritime'}
                  </p>
                </div>
              </div>

              {/* Quote */}
              <div className="p-4 rounded-xl bg-white dark:bg-[#061822] border border-slate-200 dark:border-[#134659] mb-6 italic text-slate-700 dark:text-slate-200 text-xs sm:text-sm leading-relaxed">
                {t.about.quote}
              </div>

              {/* Executive Credentials List */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                  <BookmarkCheck className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                  <span>
                    {currentLang === 'ar' ? 'الاعتمادات والمؤهلات الرئيسية :' : currentLang === 'en' ? 'Key Accreditations & Qualifications:' : 'Accréditations & Qualifications Clés :'}
                  </span>
                </h4>
                <ul className="space-y-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                  {t.about.credentials.map((cred: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                      <span>{cred}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Verified Trust Badge */}
              <div className="mt-6 pt-4 border-t border-slate-200 dark:border-[#134659] flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1.5 text-cyan-700 dark:text-cyan-300 font-medium">
                  <ShieldCheck className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                  <span>
                    {currentLang === 'ar' ? 'هيئة تدقيق وخبرة معتمدة' : currentLang === 'en' ? 'Certified Audit & Consulting Body' : 'Organisme Auditeur Certifié'}
                  </span>
                </span>
                <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
                  REF: CHC-EXP-INTL
                </span>
              </div>

            </div>
          </div>

          {/* Right Narrative & Core Values (Right 7 Cols) */}
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-4 text-slate-600 dark:text-slate-300 text-sm sm:text-base leading-relaxed">
              {branding.aboutText ? (
                <p className="text-slate-900 dark:text-white font-medium text-base sm:text-lg whitespace-pre-line">
                  {branding.aboutText}
                </p>
              ) : (
                <>
                  <p className="text-slate-900 dark:text-white font-medium text-base sm:text-lg">
                    {t.about.p1}
                  </p>
                  <p>
                    {t.about.p2}
                  </p>
                </>
              )}
            </div>

            {/* Core Values Bento Grid */}
            <div className="space-y-4 pt-2">
              <h3 className="text-base sm:text-lg font-bold font-montserrat text-slate-900 dark:text-white flex items-center gap-2">
                <Globe2 className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                <span>{t.about.valuesTitle}</span>
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {t.about.values.map((val: { title: string; desc: string }, idx: number) => (
                  <div 
                    key={idx}
                    className="p-5 rounded-2xl bg-slate-50 dark:bg-[#09222E] border border-slate-200 dark:border-[#134659] hover:border-cyan-400 dark:hover:border-cyan-500/50 transition-all hover:-translate-y-0.5 shadow-sm dark:shadow-md"
                  >
                    <div className="w-8 h-8 rounded-lg bg-cyan-500/15 flex items-center justify-center text-cyan-600 dark:text-cyan-400 mb-3 font-bold text-sm">
                      0{idx + 1}
                    </div>
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-1.5">
                      {val.title}
                    </h4>
                    <p className="text-xs text-slate-600 dark:text-slate-300 leading-normal">
                      {val.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* International Presence Footnote */}
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-gradient-to-r dark:from-[#09222E] dark:to-[#061822] border border-slate-200 dark:border-[#134659] flex flex-wrap items-center justify-between gap-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-cyan-500/15 text-cyan-600 dark:text-cyan-400">
                  <Briefcase className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-900 dark:text-white">
                    {currentLang === 'ar' ? 'شبكة عمليات دولية' : currentLang === 'en' ? 'Operational International Network' : 'Réseau International Opérationnel'}
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">
                    {currentLang === 'ar' ? 'تدخلات ميدانية ومهام تدقيق متعددة الدول' : currentLang === 'en' ? 'On-site missions & multi-country audit audits' : "Interventions sur site & missions d'audit multi-pays"}
                  </div>
                </div>
              </div>
              <div className="text-xs font-semibold text-cyan-700 dark:text-cyan-300">
                {currentLang === 'ar' ? 'فرنسا • الجزائر • تونس • الإمارات' : currentLang === 'en' ? 'France • Algeria • Tunisia • UAE' : 'France • Algérie • Tunisie • EAU'}
              </div>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};
